/** Money · Xero desk tiles — reads the connected Xero (single tenant) and
 *  shows the live money picture. Never writes anything to Xero.
 *
 *  Two tiles:
 *    - MoneyXeroTile        (compact) — 3 KPIs: bank, owed to you, overdue.
 *    - MoneyXeroDetailTile  (roomy)   — bank, receivables + overdue split,
 *                                        payables, net, month income vs
 *                                        expenses, oldest overdue.
 *
 *  Both:
 *    - Carry the Xero mark (hosted at /xero-mark.svg) so it's visible on
 *      the tile — MJ's requirement.
 *    - Tap anywhere → opens the overdue chase-list modal (defined below).
 *    - Show "Reconnect Xero" state when the connection is dead — ties into
 *      the existing health-dot / /api/admin/xero/start flow.
 *    - Data is fetched from /api/admin/xero/money_summary (server-side
 *      cache 10 min); a manual "refresh" pill busts the cache with ?force=1.
 *    - Any figure the current Xero scope can't return is shown as a
 *      "Reconnect for this" chip rather than $0 — never fabricate.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD } from './V2DeskTiles';

// ── Live-from-Xero freshness stamp ─────────────────────────────────────
const ago = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const AUD = (v) => `$${Math.round(Number(v || 0)).toLocaleString('en-AU')}`;
const AUDsigned = (v) => (Number(v) >= 0 ? '+' : '−') + `$${Math.abs(Math.round(Number(v || 0))).toLocaleString('en-AU')}`;

// Xero brand mark — served locally at /xero-mark.svg.
export const XeroMark = ({ size = 22 }) => (
  <img src="/xero-mark.svg" alt="Xero" data-testid="xero-mark"
       style={{ width: size, height: size, display: 'block', flexShrink: 0,
                filter: 'drop-shadow(0 2px 4px rgba(123,176,236,0.35))' }} />
);

// Kick off the Xero OAuth reconnect flow. Must NOT be a plain <a href> —
// /api/admin/xero/start requires admin_required (JWT bearer) and returns
// JSON `{authorization_url}`. A raw browser click has no Authorization
// header → 401 `unauthenticated` (this bit MJ once). Instead: fetch it
// with the auth header attached by `req`, then hand-off the browser to
// the returned Xero authorize URL. Mirror of the flow that already
// works in V2ConsultingQuotesPage.
//
// `returnTo` defaults to `/admin/desk` — the Desk is where the Money tile
// lives, so that's where MJ should land after consenting. We used to send
// him to `/admin/os` which is the CLIENT REPORTS list (a completely
// different page); that bug is fixed here. The backend callback tags
// `?xero=connected` on the URL so the destination page can force-refresh
// and toast the success.
const startXeroReconnect = async (req, returnTo = '/admin/desk') => {
  try {
    const d = await req('GET', `/api/admin/xero/start?return_to=${encodeURIComponent(returnTo)}`);
    if (d && d.authorization_url) {
      window.location.href = d.authorization_url;
      return;
    }
    window.alert('Xero reconnect: no authorization URL returned. Check /api/admin/xero/status.');
  } catch (e) {
    window.alert(`Xero reconnect failed: ${e?.message || 'unknown error'}. Check backend logs / XERO_CLIENT_ID env.`);
  }
};

// ── Reusable KPI cell ─────────────────────────────────────────────────
// When a specific figure requires a Xero scope MJ hasn't granted yet, we
// render an ACTIONABLE reconnect button in the value slot (not just a
// passive text hint) so MJ can see it's a one-click fix — not a broken
// state. Owed/Overdue on the same tile still show real numbers, so the
// tile is never a blanket "Connect Xero" prompt just because one field
// is scope-gated.
const Kpi = ({ label, value, color = TXT, sub = null, testId, missing = false, alt = null, onReconnect = null }) => (
  <div data-testid={testId} style={{ minWidth: 0 }}>
    <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>{label}</p>
    {missing ? (
      <div style={{ marginTop: 4 }}>
        <p style={{ margin: 0, fontFamily: HEAD, fontSize: 15, fontWeight: 700, color: SUB }}>—</p>
        <button data-testid={`${testId}-reconnect`}
           onClick={(e) => { e.stopPropagation(); onReconnect && onReconnect(); }}
           style={{ position: 'relative', zIndex: 2,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    marginTop: 4, padding: '3px 8px', borderRadius: 999,
                    background: 'rgba(123,176,236,0.14)', border: '1px solid rgba(123,176,236,0.55)',
                    color: '#7BB0EC', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, fontWeight: 800,
                    letterSpacing: '0.02em', cursor: 'pointer', whiteSpace: 'nowrap' }}
           title={alt || 'Reconnect Xero to grant the reports scope so this field can populate'}>
          {alt || '↻ Reconnect for this'}
        </button>
      </div>
    ) : (
      <>
        <p style={{ margin: 0, fontFamily: HEAD, fontSize: 20, fontWeight: 800, color, lineHeight: 1.15, whiteSpace: 'nowrap' }}>{value}</p>
        {sub && <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, color: SUB, whiteSpace: 'nowrap' }}>{sub}</p>}
      </>
    )}
  </div>
);

// ── Freshness stamp — the source logo now lives in the shared tile shell
// header (V2DeskPage renders SourceMark on the LEFT for every tile), so
// the inner header just carries the tile name, live/updated timestamp,
// and the manual refresh button. No duplicated logo.
const XeroHeader = ({ label, fetchedAt, cached, stale, onRefresh, testId }) => (
  <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <p style={{ margin: 0, fontFamily: HEAD, fontSize: 12.5, fontWeight: 800, color: TXT, letterSpacing: '0.01em', flex: 1 }}>{label}</p>
    <span style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, whiteSpace: 'nowrap' }}>
      live from Xero · updated {ago(fetchedAt)}{stale ? ' · stale' : cached ? '' : ''}
    </span>
    <button className="no-drag" data-testid={`${testId}-refresh`}
      onClick={(e) => { e.stopPropagation(); onRefresh(); }}
      title="Refresh from Xero"
      style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999,
               color: SUB, padding: '2px 8px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10, cursor: 'pointer' }}>↻</button>
  </div>
);

// ── Reconnect empty state (when Xero is disconnected) ─────────────────
const XeroDisconnected = ({ reason, onReconnect, testId }) => (
  <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 12, height: '100%', flexWrap: 'wrap' }}>
    <XeroMark size={26} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontFamily: HEAD, fontSize: 13, fontWeight: 800, color: '#7BB0EC' }}>Xero disconnected</p>
      <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, lineHeight: 1.4 }}>
        {reason || 'Sign in again to see your live money picture.'}
      </p>
    </div>
    <button data-testid={`${testId}-reconnect`} onClick={onReconnect} className="no-drag"
      style={{ background: '#7BB0EC', border: 'none', borderRadius: 999, padding: '6px 14px',
               fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, fontWeight: 800, color: '#fff', cursor: 'pointer' }}>
      Reconnect Xero
    </button>
  </div>
);
// ── Data hook — fetches money_summary, caches per-page ────────────────
export const useMoneySummary = (req) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback((force = false) => {
    setBusy(true);
    req('GET', `/api/admin/xero/money_summary${force ? '?force=1' : ''}`)
      .then((d) => setData(d))
      .catch((e) => setData({ connected: false, reason: e?.message || 'Fetch failed' }))
      .finally(() => setBusy(false));
  }, [req]);
  useEffect(() => {
    // First mount → check the URL. If we just landed back from the Xero
    // OAuth callback (`?xero=connected`), the backend has already busted
    // the money_summary cache, but do a `force=1` load anyway so the tile
    // NEVER flashes a stale "not connected" state before flipping green.
    const url = new URL(window.location.href);
    const justConnected = url.searchParams.get('xero') === 'connected';
    load(justConnected);
    // Refresh every 10 min in the background — matches server cache TTL.
    const t = setInterval(() => load(false), 600000);
    return () => clearInterval(t);
  }, [load]);
  return { data, busy, refresh: () => load(true) };
};

// ── The chase-list modal — opened by tapping either tile ───────────────
export const OverdueChaseModal = ({ data, onClose }) => {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', k); document.body.style.overflow = ''; };
  }, [onClose]);
  const rows = (data?.chase || []);
  return (
    <div data-testid="xero-chase-modal" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(14,15,17,0.86)',
               backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#17181B', border: `1px solid ${BORDER}`, borderRadius: 20,
                 width: '100%', maxWidth: 720, maxHeight: '86vh', overflow: 'hidden',
                 boxShadow: '0 40px 120px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <XeroMark size={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: TXT }}>Overdue · chase list</p>
            <p style={{ margin: '3px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB }}>
              {rows.length === 0 ? 'Nothing overdue. Rare air.'
                : `${rows.length} invoice${rows.length === 1 ? '' : 's'} · total ${AUD(data?.receivables?.overdue?.total || 0)} · oldest first`}
            </p>
          </div>
          <button data-testid="xero-chase-close" onClick={onClose} className="no-drag"
            style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: SUB,
                     width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>
        {/* Rows */}
        <div className="studio-scroll" style={{ overflowY: 'auto', padding: '10px 14px 18px' }}>
          {rows.length === 0 && (
            <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 12, color: SUB, padding: '30px 6px', textAlign: 'center' }}>
              🎉 Nothing overdue. Well done.
            </p>
          )}
          {rows.map((r, i) => (
            <div key={r.invoice_id || i} data-testid={`chase-row-${i}`}
              style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 10px',
                       borderRadius: 12, marginTop: i === 0 ? 0 : 6,
                       background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: HEAD, fontSize: 13.5, fontWeight: 800, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.contact || '—'}
                </p>
                <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, color: SUB }}>
                  {r.invoice_number || '—'}{r.reference ? ` · ${r.reference}` : ''}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, fontFamily: HEAD, fontSize: 14, fontWeight: 800, color: AMBER, whiteSpace: 'nowrap' }}>{AUD(r.amount_due)}</p>
                <p style={{ margin: '2px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5,
                            color: (r.days_overdue || 0) > 30 ? MAGENTA : AMBER }}>
                  {r.days_overdue > 0 ? `${r.days_overdue} days over` : 'due today'}
                </p>
              </div>
              <a data-testid={`chase-row-open-${i}`} href={r.link} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ background: '#7BB0EC', color: '#fff', borderRadius: 999,
                         padding: '6px 12px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 10.5, fontWeight: 800,
                         textDecoration: 'none', whiteSpace: 'nowrap' }}>
                Open in Xero →
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Compact tile: "MONEY · XERO" ──────────────────────────────────────
export const MoneyXeroTile = ({ ctx }) => {
  const { data, busy, refresh } = useMoneySummary(ctx.req);
  const [modal, setModal] = useState(false);
  const reconnect = useCallback(() => startXeroReconnect(ctx.req, '/admin/desk'), [ctx.req]);
  if (!data) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 }}>Reading Xero…</p>;
  if (data.connected === false) {
    return <XeroDisconnected reason={data.reason} testId="money-xero-tile" onReconnect={reconnect} />;
  }
  const bank = data.bank_balance;
  const bankMissing = !!(data._missing && data._missing.bank_balance);
  const owed = data.receivables?.total || 0;
  const overCount = data.receivables?.overdue?.count || 0;
  const overTotal = data.receivables?.overdue?.total || 0;
  const openChase = (e) => { e.stopPropagation(); setModal(true); };
  return (
    <div data-testid="money-xero-tile" onClick={openChase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, cursor: 'pointer' }}>
      <XeroHeader label="Money · Xero" fetchedAt={data.fetched_at}
        cached={data.cached} stale={data.stale} testId="money-xero-header"
        onRefresh={refresh} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 14, flex: 1, alignItems: 'start', position: 'relative' }}>
        <Kpi label="Bank balance" value={AUD(bank)} color={TEAL} testId="mx-bank"
             missing={bankMissing} alt="↻ 1-click reconnect" onReconnect={reconnect} />
        <Kpi label="Owed to you" value={AUD(owed)} color={TXT} testId="mx-owed"
             sub={data.receivables ? 'receivables · live' : null} />
        <Kpi label="Overdue"
             value={`${overCount} · ${AUD(overTotal)}`}
             color={overCount > 0 ? MAGENTA : SUB}
             testId="mx-overdue"
             sub={overCount > 0 ? 'tap → chase list' : 'nothing overdue'} />
      </div>
      {bankMissing && (
        <p data-testid="mx-scope-hint" style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9,
                                                 color: '#7BB0EC', textAlign: 'right' }}>
          Xero connected · bank balance needs the reports scope
        </p>
      )}
      {busy && <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9, color: SUB }}>refreshing…</p>}
      {modal && <OverdueChaseModal data={data} onClose={() => setModal(false)} />}
    </div>
  );
};

// ── Detailed tile: "MONEY · XERO — DETAIL" ────────────────────────────
export const MoneyXeroDetailTile = ({ ctx }) => {
  const { data, busy, refresh } = useMoneySummary(ctx.req);
  const [modal, setModal] = useState(false);
  const reconnect = useCallback(() => startXeroReconnect(ctx.req, '/admin/desk'), [ctx.req]);
  if (!data) return <p style={{ fontFamily: 'Source Sans 3, sans-serif', fontSize: 11.5, color: SUB, margin: 0 }}>Reading Xero…</p>;
  if (data.connected === false) {
    return <XeroDisconnected reason={data.reason} testId="money-xero-detail-tile" onReconnect={reconnect} />;
  }
  const bankMissing = !!(data._missing && data._missing.bank_balance);
  const owed = data.receivables?.total || 0;
  const overCount = data.receivables?.overdue?.count || 0;
  const overTotal = data.receivables?.overdue?.total || 0;
  const oldest = data.receivables?.overdue?.oldest;
  const payable = data.payables?.total || 0;
  const payCount = data.payables?.count || 0;
  const net = data.net_position || 0;
  const income = data.month?.income || 0;
  const expenses = data.month?.expenses || 0;
  const openChase = (e) => { e.stopPropagation(); setModal(true); };
  return (
    <div data-testid="money-xero-detail-tile" onClick={openChase}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, cursor: 'pointer' }}>
      <XeroHeader label="Money · Xero — Detail" fetchedAt={data.fetched_at}
        cached={data.cached} stale={data.stale} testId="money-xero-detail-header"
        onRefresh={refresh} />
      <div className="studio-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 12 }}>
          <Kpi label="Bank balance" value={AUD(data.bank_balance)} color={TEAL} testId="mxd-bank"
               missing={bankMissing} alt="↻ 1-click reconnect" onReconnect={reconnect}
               sub={data.bank_account_count != null ? `${data.bank_account_count} account${data.bank_account_count === 1 ? '' : 's'}` : null} />
          <Kpi label="Owed to you" value={AUD(owed)} color={TXT} testId="mxd-owed"
               sub={overCount > 0
                 ? <><b style={{ color: MAGENTA }}>{overCount}</b> · <b style={{ color: MAGENTA }}>{AUD(overTotal)}</b> overdue</>
                 : 'nothing overdue'} />
          <Kpi label="You owe" value={AUD(payable)} color={AMBER} testId="mxd-payables"
               sub={`${payCount} bill${payCount === 1 ? '' : 's'} awaiting`} />
          <Kpi label="Net position" value={AUDsigned(net)}
               color={net >= 0 ? TEAL : MAGENTA} testId="mxd-net"
               sub="owed to you − you owe" />
        </div>
        {/* Month income vs expenses */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, marginBottom: 12,
                      background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '10px 14px' }}>
          <div>
            <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>This month · income</p>
            <p data-testid="mxd-month-income" style={{ margin: '2px 0 0', fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: TEAL }}>▲ {AUD(income)}</p>
          </div>
          <div>
            <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: SUB }}>This month · expenses</p>
            <p data-testid="mxd-month-expenses" style={{ margin: '2px 0 0', fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: MAGENTA }}>▼ {AUD(expenses)}</p>
          </div>
        </div>
        {/* Oldest overdue */}
        {oldest ? (
          <div data-testid="mxd-oldest-overdue"
            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(236,124,36,0.06)',
                     border: `1px solid rgba(236,124,36,0.35)`, borderRadius: 12, padding: '10px 14px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: 'Source Sans 3, sans-serif', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: MAGENTA }}>Oldest overdue</p>
              <p style={{ margin: '3px 0 0', fontFamily: HEAD, fontSize: 13, fontWeight: 800, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {oldest.contact}<span style={{ color: SUB, fontWeight: 500 }}> · </span>
                <span style={{ color: MAGENTA }}>{oldest.days_overdue} days</span>
              </p>
            </div>
            <p style={{ margin: 0, fontFamily: HEAD, fontSize: 15, fontWeight: 800, color: AMBER, whiteSpace: 'nowrap' }}>{AUD(oldest.amount_due)}</p>
          </div>
        ) : (
          <p style={{ margin: '4px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 11, color: SUB, textAlign: 'center' }}>
            No overdue invoices. Well done.
          </p>
        )}
      </div>
      <p style={{ margin: '6px 0 0', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5, color: SUB, textAlign: 'right' }}>
        {busy ? 'refreshing…' : 'tap → open overdue chase-list'}
      </p>
      {modal && <OverdueChaseModal data={data} onClose={() => setModal(false)} />}
    </div>
  );
};
